import { useEffect, useState, useMemo } from 'react';
import { useParams, Link } from 'react-router-dom';
import { collection, doc, getDoc, getDocs, query, where } from 'firebase/firestore';
import { db } from '@/lib/firebase';
import { useAuth } from '@/contexts/AuthContext';
import { Button } from '@/components/ui/Button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/Card';
import { getCommercialName } from '@/config/batteries';
import { getExpertBenchmark } from '@/config/tests';
import type { RoleType } from '@/config/tests';
import { computeAffinityInterpretation, getMatchLabel } from '@/lib/affinityInterpretation';
import { Info } from 'lucide-react';
import {
  getDecisionStyleLabel,
  getML8LevelLabel,
  getRA10RhSummary,
  computePCL10DerivedConfianza,
  computeCriterioOperativoNormasDerived,
  computeEstiloDecisionesDerived,
  APO6_NOTE,
  CONFIABILIDAD_ETICA_DERIVED_NOTE,
  CRITERIO_OPERATIVO_DERIVED_NOTE,
  ESTILO_DECISIONES_DERIVED_NOTE,
  RA_10_DERIVED_NOTE,
} from '@/lib/instrumentScoring';
import type { Result } from '@/types';
import {
  calculateCulturalFit,
  computeCultureScores,
  computeCultureScoresFromResponses,
  extractCultureActualAnswersFromAssessmentDoc,
  type CultureResponse,
  type CultureScore,
} from '@/lib/cultureEngine';

export function ResultDetailPage() {
  const { attemptId } = useParams<{ attemptId: string }>();
  const { appUser, canAccessCompany } = useAuth();
  const [result, setResult] = useState<Result | null>(null);
  const [candidateName, setCandidateName] = useState('');
  const [processData, setProcessData] = useState<{ roleType?: string; jobProfile?: Record<string, unknown> } | null>(null);
  const [culturalFitPanel, setCulturalFitPanel] = useState<{
    percent: number;
    candidateScores: CultureScore;
    companyScores: CultureScore;
  } | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!attemptId || !appUser) return;
    setError(null);
    const load = async () => {
      setCulturalFitPanel(null);
      try {
        const resSnap = await getDoc(doc(db, 'results', attemptId));
        if (!resSnap.exists()) {
          setResult(null);
          return;
        }
        const data = resSnap.data();
        if (!canAccessCompany(data.companyId)) {
          setError('No tienes permiso para ver este resultado.');
          setResult(null);
          return;
        }
        setResult({
          attemptId: resSnap.id,
          companyId: data.companyId,
          scores: data.scores ?? [],
          moduleAggregates: data.moduleAggregates ?? {},
          version: data.version ?? 1,
          computedAt: data.computedAt ?? '',
        });

        try {
          const respSnap = await getDoc(doc(db, 'responses', attemptId));
          if (respSnap.exists()) {
            const answers = (respSnap.data().answers ?? {}) as Record<string, unknown>;
            const cultureAnswers = answers.CULTURE_FIT as Record<string, number> | undefined;
            if (cultureAnswers && typeof cultureAnswers === 'object' && Object.keys(cultureAnswers).length > 0) {
              const cultureSnap = await getDocs(
                query(collection(db, 'culture_assessments'), where('companyId', '==', data.companyId))
              );
              const responses: CultureResponse[] = [];
              cultureSnap.forEach((docSnap) => {
                const row = docSnap.data() as Record<string, unknown>;
                if (row.status !== 'completed') return;
                const actual = extractCultureActualAnswersFromAssessmentDoc(row);
                if (!actual) return;
                responses.push({
                  department: typeof row.department === 'string' ? row.department.trim() : undefined,
                  answers: actual,
                });
              });
              const companyScores = computeCultureScoresFromResponses(responses);
              const candidateScores = computeCultureScores(cultureAnswers);
              const percent = calculateCulturalFit(candidateScores, companyScores);
              setCulturalFitPanel({ percent, candidateScores, companyScores });
            }
          }
        } catch (cultureErr) {
          if (import.meta.env?.DEV) console.warn('[ResultDetailPage] Ajuste cultural no disponible:', cultureErr);
          setCulturalFitPanel(null);
        }

        const attSnap = await getDoc(doc(db, 'attempts', attemptId));
        if (attSnap.exists()) {
          const att = attSnap.data();
          if (att.candidateId) {
            const cid = att.candidateId;
            const cidStr = typeof cid === 'string' ? cid : (cid as { id?: string })?.id;
            if (cidStr) {
              const cSnap = await getDoc(doc(db, 'candidates', cidStr));
              if (cSnap.exists()) {
                const c = cSnap.data();
                setCandidateName((c.fullName as string) || (c.email as string) || 'Candidato');
              }
            }
          }
          if (att.processId) {
            const procSnap = await getDoc(doc(db, 'processes', att.processId));
            if (procSnap.exists()) {
              const proc = procSnap.data();
              setProcessData({
                roleType: proc.roleType as string | undefined,
                jobProfile: (proc.jobProfile as Record<string, unknown>) ?? undefined,
              });
            }
          }
        }
      } catch (e) {
        const msg = e instanceof Error ? e.message : String(e);
        setError(/permission|denied/i.test(msg) ? 'Sin permiso para ver este resultado.' : msg);
        setResult(null);
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [attemptId, appUser, canAccessCompany]);

  const affinityInterpretation = useMemo(() => {
    if (!result || !processData) return null;
    const jobProfile =
      processData.jobProfile ??
      (processData.roleType ? getExpertBenchmark(processData.roleType as RoleType) : null);
    if (!jobProfile) return null;
    return computeAffinityInterpretation({
      jobProfile,
      scores: result.scores.map((s) => ({
        testId: s.testId,
        scorePercent: s.scorePercent ?? (typeof s.scaleScore === 'number' ? s.scaleScore : undefined),
        scaleScore: s.scaleScore,
      })),
      moduleAggregates: result.moduleAggregates,
      roleType: processData.roleType ?? null,
    });
  }, [result, processData]);

  if (loading) return <p className="text-muted-foreground">Cargando...</p>;
  if (error) {
    return (
      <div className="space-y-2">
        <p className="text-destructive">{error}</p>
        <Button variant="ghost" size="sm" asChild>
          <Link to="/app/results">← Volver a Resultados</Link>
        </Button>
      </div>
    );
  }
  if (!result) {
    return (
      <div className="space-y-2">
        <p className="text-muted-foreground">Resultado no encontrado o aún no hay puntuaciones.</p>
        <Button variant="ghost" size="sm" asChild>
          <Link to="/app/results">← Volver a Resultados</Link>
        </Button>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <Button variant="ghost" size="sm" asChild>
          <Link to="/app/results">← Resultados</Link>
        </Button>
        <Button asChild>
          <Link to={`/app/results/${attemptId}/pdf`}>Descargar PDF</Link>
        </Button>
      </div>
      <h1 className="text-2xl font-semibold">Resultado — {candidateName}</h1>
      {culturalFitPanel && (
        <Card>
          <CardHeader>
            <CardTitle className="text-base">Ajuste cultural organizacional</CardTitle>
            <p className="text-sm text-muted-foreground">
              Comparación entre el perfil cultural del candidato (versión corta) y el perfil actual agregado de la empresa,
              calculada solo al consultar este resultado con permisos de RH.
            </p>
          </CardHeader>
          <CardContent className="space-y-3">
            <p className="text-3xl font-semibold tabular-nums">{culturalFitPanel.percent}%</p>
            <p className="text-xs text-muted-foreground">
              % de dominancia (base 100): candidato Clan {culturalFitPanel.candidateScores.clan}, Adocracia{' '}
              {culturalFitPanel.candidateScores.adhocracy}, Mercado {culturalFitPanel.candidateScores.market}, Jerarquía{' '}
              {culturalFitPanel.candidateScores.hierarchy} · empresa Clan {culturalFitPanel.companyScores.clan}, Adocracia{' '}
              {culturalFitPanel.companyScores.adhocracy}, Mercado {culturalFitPanel.companyScores.market}, Jerarquía{' '}
              {culturalFitPanel.companyScores.hierarchy}
            </p>
          </CardContent>
        </Card>
      )}
      {affinityInterpretation && (
        <Card>
          <CardHeader>
            <CardTitle className="text-base flex items-center gap-2">
              Interpretación de la afinidad al puesto
              <span
                className="inline-flex items-center gap-1 text-sm font-normal text-muted-foreground"
                title="La afinidad indica qué tan alineado está el candidato con el perfil ideal para este puesto. Cada dimensión tiene un peso según su relevancia."
              >
                <Info className="h-4 w-4" />
              </span>
            </CardTitle>
            <p className="text-sm text-muted-foreground">
              La afinidad ({affinityInterpretation.affinityPercent}%) indica qué tan alineado está el candidato con el perfil ideal para{' '}
              <strong>{affinityInterpretation.roleLabel}</strong>.
            </p>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="overflow-x-auto">
              <table className="w-full text-sm border-collapse">
                <thead>
                  <tr className="border-b">
                    <th className="text-left py-2 pr-4 font-medium">Dimensión</th>
                    <th className="text-left py-2 pr-4 font-medium">Importancia</th>
                    <th className="text-left py-2 pr-4 font-medium">Esperado</th>
                    <th className="text-left py-2 pr-4 font-medium">Candidato</th>
                    <th className="text-left py-2 font-medium">Coincide</th>
                  </tr>
                </thead>
                <tbody>
                  {affinityInterpretation.items.map((i) => (
                    <tr key={i.name} className="border-b last:border-0">
                      <td className="py-2 pr-4">{i.name}</td>
                      <td className="py-2 pr-4 text-muted-foreground">{i.importance}</td>
                      <td className="py-2 pr-4 text-muted-foreground">{i.expected}</td>
                      <td className="py-2 pr-4">{i.candidate}</td>
                      <td className="py-2">
                        <span
                          className={
                            i.match === 'yes' || i.match === 'exceeds'
                              ? 'text-green-600 dark:text-green-500'
                              : i.match === 'below'
                                ? 'text-amber-600 dark:text-amber-500'
                                : 'text-muted-foreground'
                          }
                        >
                          {getMatchLabel(i.match)}
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <p className="text-sm text-muted-foreground">
              <strong>Resumen:</strong> {affinityInterpretation.summaryText}
            </p>
          </CardContent>
        </Card>
      )}
      <Card>
        <CardHeader>
          <CardTitle className="text-base">Puntuaciones por prueba</CardTitle>
          <p className="text-sm text-muted-foreground">
            Nombres comerciales. Uso exclusivo para RH; no constituyen diagnóstico.
          </p>
        </CardHeader>
        <CardContent>
          <ul className="space-y-4">
            {result.scores.map((s) => {
              const agg = result.moduleAggregates[s.testId] as Record<string, unknown> | undefined;
              const decisionStyle = agg?.decisionStyle as Record<string, unknown> | undefined;
              const ml8Labels = s.testId === 'ML_8' ? (agg?.labels as Record<string, string> | undefined) : undefined;
              const ml8Derived = s.testId === 'ML_8' ? (agg?.derived as Record<string, { score: number; level: string; text: string }> | undefined) : undefined;
              return (
                <li key={s.testId} className="border-b pb-4 last:border-0">
                  <div className="flex flex-wrap justify-between gap-2">
                    <span className="font-medium">
                      {s.testId === 'RA_10' ? getCommercialName('estilo_trabajo_organizacion') : (s.commercialName ?? getCommercialName(s.testId))}
                    </span>
                    <span className="text-muted-foreground">
                      {(s.scaleScore ?? s.rawScore) != null &&
                        (['JK_OFFICE_ADMIN', 'JK_SALES_RETAIL', 'APO_6'].includes(s.testId)
                          ? `Porcentaje: ${s.scaleScore ?? s.rawScore}%`
                          : `Puntuación: ${s.scaleScore ?? s.rawScore}`)}
                      {(s.bandLabel ?? s.band) && ` · ${s.bandLabel ?? s.band}`}
                    </span>
                  </div>
                  {s.rhSummary && !ml8Labels && s.testId !== 'APO_6' && (
                    <p className="mt-2 text-sm text-muted-foreground">{s.rhSummary}</p>
                  )}
                  {s.testId === 'APO_6' && (
                    <p className="mt-2 text-xs text-muted-foreground">{APO6_NOTE}</p>
                  )}
                  {s.testId === 'MINI_IPIP_20' && (() => {
                    const miniAgg = agg as {
                      dimensions?: Record<string, { percent: number; level: string; text: string; normUsed?: string; nNorm?: number }>;
                    } | undefined;
                    const dims = miniAgg?.dimensions;
                    if (!dims) return null;
                    const firstDim = Object.values(dims)[0];
                    const normLabel = firstDim?.normUsed
                      ? { company: 'Empresa', industry: 'Industria', country: 'País', global: 'Global', global_seed: 'Semilla', fallback: 'Fallback' }[firstDim.normUsed] ?? firstDim.normUsed
                      : 'Fallback';
                    const nNorm = firstDim?.nNorm ?? 0;
                    const DIM_NAMES: Record<string, string> = {
                      responsabilidadOrden: 'Responsabilidad y Orden',
                      estabilidadEmocional: 'Estabilidad Emocional',
                      energiaSocial: 'Energía Social',
                      cooperacion: 'Cooperación',
                      aperturaAdaptabilidad: 'Apertura y Adaptabilidad',
                    };
                    const order = ['responsabilidadOrden', 'estabilidadEmocional', 'energiaSocial', 'cooperacion', 'aperturaAdaptabilidad'];
                    return (
                      <div className="mt-3 space-y-4">
                        <div className="rounded-md bg-muted/50 p-3 text-sm space-y-3">
                          {order.map((key) => {
                            const d = dims[key];
                            if (!d) return null;
                            return (
                              <div key={key}>
                                <div className="flex flex-wrap justify-between gap-2">
                                  <span className="font-medium">{DIM_NAMES[key] ?? key}</span>
                                  <span className="text-muted-foreground">
                                    {d.percent}% · {d.level}
                                  </span>
                                </div>
                                <p className="mt-1 text-muted-foreground text-xs">{d.text}</p>
                                <div className="mt-1 h-2 rounded-full bg-muted overflow-hidden">
                                  <div
                                    className="h-full bg-primary/70 rounded-full transition-all"
                                    style={{ width: `${Math.min(100, Math.max(0, d.percent))}%` }}
                                  />
                                </div>
                              </div>
                            );
                          })}
                          <p className="mt-3 text-xs text-muted-foreground border-t pt-2">
                            Referencia: {normLabel} (n={nNorm})
                          </p>
                          <p className="text-xs text-muted-foreground">
                            Perfil laboral breve basado en autoinforme. Interpretar junto con entrevista y requisitos del puesto.
                          </p>
                        </div>
                      </div>
                    );
                  })()}
                  {ml8Labels && (
                    <div className="mt-3 space-y-4">
                      <div className="rounded-md bg-muted/50 p-3 text-sm">
                        <p className="font-medium">Perfil de motivación</p>
                        <ul className="mt-2 list-disc pl-4 space-y-1 text-muted-foreground">
                          <li>Estabilidad ↔ Crecimiento: {ml8Labels.label_dim1 ?? ml8Labels.dim1Label ?? '—'}</li>
                          <li>Compensación ↔ Ambiente: {ml8Labels.label_dim2 ?? ml8Labels.dim2Label ?? '—'}</li>
                          <li>Autonomía ↔ Guía: {ml8Labels.label_dim3 ?? ml8Labels.dim3Label ?? '—'}</li>
                          <li>Rutina ↔ Variedad: {ml8Labels.label_dim4 ?? ml8Labels.dim4Label ?? '—'}</li>
                        </ul>
                        <p className="mt-2 text-xs text-muted-foreground">
                          Este perfil describe preferencias de motivación laboral; no evalúa desempeño ni diagnóstico.
                        </p>
                      </div>
                      {ml8Derived && (
                        <div className="space-y-3">
                          <p className="font-medium text-sm">Indicadores derivados</p>
                          {ml8Derived.persistenciaPresion && (
                            <div className="rounded-md bg-muted/50 p-3 text-sm">
                              <p className="font-medium">Persistencia y Desempeño bajo Presión</p>
                              <p className="mt-1 text-muted-foreground">
                                <strong>{getML8LevelLabel(ml8Derived.persistenciaPresion.level)}</strong> — {ml8Derived.persistenciaPresion.text}
                              </p>
                              <p className="mt-2 text-xs text-muted-foreground">
                                Indicador derivado a partir de preferencias motivacionales. No es una medida directa de desempeño.
                              </p>
                            </div>
                          )}
                          {ml8Derived.interaccionProfesional && (
                            <div className="rounded-md bg-muted/50 p-3 text-sm">
                              <p className="font-medium">Competencias de Interacción Profesional</p>
                              <p className="mt-1 text-muted-foreground">
                                <strong>{getML8LevelLabel(ml8Derived.interaccionProfesional.level)}</strong> — {ml8Derived.interaccionProfesional.text}
                              </p>
                              <p className="mt-2 text-xs text-muted-foreground">
                                Indicador derivado a partir de preferencias motivacionales. No es una medida directa de desempeño.
                              </p>
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  )}
                  {decisionStyle && (
                    <div className="mt-3 rounded-md bg-muted/50 p-3 text-sm">
                      <p className="font-medium">Estilo de decisión (PED)</p>
                      <p className="mt-1 text-muted-foreground">
                        {(decisionStyle.rhStyleSummary as string) ?? ''}
                      </p>
                      <p className="mt-2 text-xs text-muted-foreground">
                        Dominante: {getDecisionStyleLabel(String(decisionStyle.dominantStyle ?? ''))} · 
                        Secundario: {getDecisionStyleLabel(String(decisionStyle.secondaryStyle ?? ''))} · 
                        Menor: {getDecisionStyleLabel(String(decisionStyle.riskStyle ?? ''))}
                      </p>
                      <p className="mt-1 text-xs">
                        Apego normativo: {(decisionStyle.apegoNormativo as number) ?? 0} · 
                        Autonomía práctica: {(decisionStyle.autonomiaPractica as number) ?? 0} · 
                        Dependencia jerárquica: {(decisionStyle.dependenciaJerarquica as number) ?? 0} · 
                        Evitación del riesgo: {(decisionStyle.evitacionRiesgo as number) ?? 0}
                      </p>
                    </div>
                  )}
                </li>
              );
            })}
            {(() => {
              const ra10Agg = result.moduleAggregates.RA_10 as Record<string, unknown> | undefined;
              const ra10Derived = ra10Agg?.derived as Record<string, { derived?: boolean; percent?: number; adecuacion?: string; text?: string }> | undefined;
              let responsabilidadDerived = ra10Derived?.responsabilidadControl;
              if (!responsabilidadDerived && ra10Agg) {
                const score50 = ra10Agg.score50 as number | undefined;
                const bandLabel = ra10Agg.bandLabel as string | undefined;
                const bandKey = ra10Agg.bandKey as string | undefined;
                if (score50 != null) {
                  responsabilidadDerived = {
                    percent: Math.round((score50 / 50) * 100),
                    adecuacion: bandLabel ?? '—',
                    text: bandKey ? getRA10RhSummary(bandKey) : undefined,
                  };
                }
              }
              if (!ra10Agg || !responsabilidadDerived) return null;
              return (
                <li key="responsabilidad_control_derived" className="border-b pb-4 last:border-0">
                  <div className="flex flex-wrap justify-between gap-2">
                    <span className="font-medium">Responsabilidad y Control en el Trabajo</span>
                    <span className="text-muted-foreground">
                      Porcentaje: {responsabilidadDerived.percent ?? '—'}% · Adecuación: {responsabilidadDerived.adecuacion ?? '—'}
                    </span>
                  </div>
                  {responsabilidadDerived.text && (
                    <p className="mt-2 text-sm text-muted-foreground">{responsabilidadDerived.text}</p>
                  )}
                  <p className="mt-2 text-xs text-muted-foreground">
                    {RA_10_DERIVED_NOTE}
                  </p>
                </li>
              );
            })()}
            {(() => {
              const pcl10Entry = result.scores.find((s) => s.testId === 'PCL_10');
              const totalPCL = pcl10Entry
                ? (pcl10Entry.rawScore ?? pcl10Entry.scaleScore ?? 0)
                : (result.moduleAggregates.PCL_10 as Record<string, unknown> | undefined)?.totalScore as number | undefined;
              if (totalPCL == null || totalPCL < 10) return null;
              const derived = computePCL10DerivedConfianza(totalPCL);
              return (
                <li key="confiabilidad_etica_derived" className="border-b pb-4 last:border-0">
                  <div className="flex flex-wrap justify-between gap-2">
                    <span className="font-medium">Confiabilidad y Ética Profesional</span>
                    <span className="text-muted-foreground">
                      Nivel: {derived.level} · Indicador: {derived.trustPercent}%
                    </span>
                  </div>
                  <p className="mt-2 text-sm text-muted-foreground">{derived.text}</p>
                  <p className="mt-2 text-xs text-muted-foreground">
                    {CONFIABILIDAD_ETICA_DERIVED_NOTE}
                  </p>
                </li>
              );
            })()}
            {(() => {
              const hasRfb = result.scores.some((s) => s.testId === 'RFB_SJ_8') || result.moduleAggregates.RFB_SJ_8;
              if (!hasRfb) return null;
              const rfbAgg = result.moduleAggregates.RFB_SJ_8 as Record<string, unknown> | undefined;
              const persistedEstilo = rfbAgg?.derived as Record<string, { label: string; text: string; noteOptional?: string }> | undefined;
              const estiloDerived = persistedEstilo?.estiloDecisiones;
              const firstChoices = rfbAgg?.firstChoices as string[] | undefined;
              const hasFirstChoices = firstChoices && firstChoices.length >= 8;
              const derived =
                estiloDerived ??
                (hasFirstChoices ? computeEstiloDecisionesDerived(firstChoices) : null);
              if (!derived) {
                if (import.meta.env.DEV && !estiloDerived && !hasFirstChoices) {
                  console.debug('[Estilo Decisiones] Datos insuficientes para derivado: firstChoices no persistidos.');
                }
                return null;
              }
              return (
                <li key="estilo_decisiones_derived" className="border-b pb-4 last:border-0">
                  <div className="flex flex-wrap justify-between gap-2">
                    <span className="font-medium">Estilo de Toma de Decisiones Laborales</span>
                    <span className="text-muted-foreground">{derived.label}</span>
                  </div>
                  <p className="mt-2 text-sm text-muted-foreground">{derived.text}</p>
                  {derived.noteOptional && (
                    <p className="mt-2 text-sm text-amber-600 dark:text-amber-500">
                      {derived.noteOptional}
                    </p>
                  )}
                  <p className="mt-2 text-xs text-muted-foreground">
                    {ESTILO_DECISIONES_DERIVED_NOTE}
                  </p>
                </li>
              );
            })()}
            {(() => {
              const rfbEntry = result.scores.find((s) => s.testId === 'RFB_SJ_8');
              const rfbAgg = result.moduleAggregates.RFB_SJ_8 as Record<string, unknown> | undefined;
              const rfbTotal =
                rfbEntry != null
                  ? (rfbEntry.rawScore ?? rfbEntry.scaleScore)
                  : (rfbAgg?.totalScore as number | undefined);
              const pcl10Entry = result.scores.find((s) => s.testId === 'PCL_10');
              const totalPCL = pcl10Entry
                ? (pcl10Entry.rawScore ?? pcl10Entry.scaleScore ?? 0)
                : (result.moduleAggregates.PCL_10 as Record<string, unknown> | undefined)?.totalScore as number | undefined;
              if (rfbTotal == null || totalPCL == null || totalPCL < 10) return null;
              const derived = computeCriterioOperativoNormasDerived(rfbTotal, totalPCL);
              return (
                <li key="criterio_operativo_derived" className="border-b pb-4 last:border-0">
                  <div className="flex flex-wrap justify-between gap-2">
                    <span className="font-medium">Criterio Operativo y Apego a Normas</span>
                    <span className="text-muted-foreground">
                      Indicador: {derived.operationalNormsIndex}% · Nivel: {derived.level}
                    </span>
                  </div>
                  <p className="mt-2 text-sm text-muted-foreground">{derived.text}</p>
                  <p className="mt-2 text-xs text-muted-foreground">
                    {CRITERIO_OPERATIVO_DERIVED_NOTE}
                  </p>
                </li>
              );
            })()}
          </ul>
        </CardContent>
      </Card>
    </div>
  );
}
